import { queries } from "@/graphql";
import { useQuery } from "@apollo/client/react";
import { useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import type { QueryClimateDataArgs } from "@/types/graphql";

type ClimateDataVariables = Partial<QueryClimateDataArgs>;

interface UseClimateDataOptions {
  skip?: boolean;
}

/**
 * Custom hook to fetch climate data.
 * @param {object} variables - The variables to be passed to the query.
 * @param {Function} [onCompleted] - Callback function to be called when the query completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the query.
 * @param {object} [options] - Additional options such as skip.
 * @returns {{data: object, total: number, loading: boolean, error: object}} - The fetched climate data, total, loading state, and error.
 */
export function useClimateData(
  variables: ClimateDataVariables,
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
  options?: UseClimateDataOptions,
) {
  // Remove country from variables if it's null
  const queryVariables = { ...variables };
  if (queryVariables.country === null) {
    delete queryVariables.country;
  }

  const skip =
    options?.skip ??
    (!queryVariables.customer ||
      !queryVariables.group ||
      !queryVariables.meterType ||
      !queryVariables.address ||
      !queryVariables.meteringPointId ||
      !queryVariables.fromDate ||
      !queryVariables.toDate ||
      !queryVariables.resolution);

  const { data, loading, error } = useQuery<any>(queries.climateData, {
    variables: queryVariables,
    skip,
    errorPolicy: "all",
  });

  // Handle success
  useEffect(() => {
    if (data?.climateData) {
      onCompleted(data.climateData);
    }
  }, [data, onCompleted]);

  // Handle error
  useEffect(() => {
    if (error) {
      onError(error);
      enqueueSnackbar(error.message, {
        variant: "error",
      });
    }
  }, [error, onError]);

  return {
    data: data?.climateData?.data || [],
    total: data?.climateData?.total || [],
    loading,
    error,
  };
}
